import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Form, Upload, Tooltip, Spin, Typography, Divider, Radio } from 'antd';
import { QuestionCircleOutlined, UploadOutlined } from '@ant-design/icons';
import { useNotify } from '@/hooks';
import { API } from '@/services/api.service';
import { cn } from '@/libs/utils';

const { Title } = Typography;

/**
 * Upload Salary Translation Form Component
 *
 * Allows users to upload Serbian salary PDF documents and receive
 * translated versions in English or Russian. Uses backend PDF processing to:
 * - Extract text from uploaded PDF
 * - Translate Serbian salary terms to English or Russian
 * - Generate new PDF with translated content
 *
 * Max file size: 5MB
 * Supported format: PDF only
 * Supported languages: English, Russian
 */
export default function UploadSalaryTranslationForm({
  // Profile: no card of its own and no title (the panel shows them).
  bare = false,
} = {}) {
  const { t } = useTranslation(['forms', 'messages']);
  const notification = useNotify();
  const [form] = Form.useForm();

  const [fileList, setFileList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [language, setLanguage] = useState('en');

  const handleRemove = () => {
    setFileList([]);
  };

  const handleFileUpload = async (file) => {
    const formData = new FormData();
    formData.append('pdf', file);
    formData.append('language', language);

    try {
      setLoading(true);
      const response = await API.companies.uploadSalaryTranslation(formData);

      // Create blob and download PDF file
      const blob = new Blob([response], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = `translated-salary-document-${language}.pdf`;
      link.click();

      window.URL.revokeObjectURL(url);

      notification.success({
        message: t('salaryTranslation.success.pdf_translated', { ns: 'messages' }),
        description: t('salaryTranslation.success.pdf_translated_desc', { ns: 'messages' }),
        placement: 'bottomRight',
      });

      // Clear the form
      setFileList([]);
      form.resetFields();
    } catch (error) {
      console.error('Error uploading salary PDF:', error);
      notification.error({
        message: t('salaryTranslation.error.pdf_translation_failed', { ns: 'messages' }),
        description: t('salaryTranslation.error.pdf_translation_failed_desc', { ns: 'messages' }),
        placement: 'bottomRight',
      });
    } finally {
      setLoading(false);
    }
  };

  const onFinish = async () => {
    if (fileList.length > 0) {
      await handleFileUpload(fileList[0]);
    } else {
      notification.error({
        message: t('salaryTranslation.error.no_file_selected', { ns: 'messages' }),
        description: t('salaryTranslation.error.upload_file_first', { ns: 'messages' }),
        placement: 'bottomRight',
      });
    }
  };

  return (
    <div
      className={cn(
        !bare && 'mx-5 my-5 max-w-3xl rounded-lg bg-white p-5 shadow-md'
      )}
    >
      {!bare && (
        <>
          <Title level={3} className="text-left">
            {t('salary_translation.title', { ns: 'forms' })}
          </Title>
          <Divider style={{ backgroundColor: '#2db1ab50', margin: '20px 0' }} />

          <p className="mb-4 text-sm text-gray-600">
            {t('salary_translation.description', { ns: 'forms' })}
          </p>
        </>
      )}

      <Form name="upload-salary-form" onFinish={onFinish} layout="vertical" form={form}>
        <Form.Item
          name="language"
          label={t('salary_translation.select_language', { ns: 'forms' })}
          initialValue="en"
        >
          <Radio.Group value={language} onChange={(e) => setLanguage(e.target.value)}>
            <Radio value="en">{t('salary_translation.english', { ns: 'forms' })}</Radio>
            <Radio value="ru">{t('salary_translation.russian', { ns: 'forms' })}</Radio>
            <Radio value="zh">{t('salary_translation.chinese', { ns: 'forms' })}</Radio>
          </Radio.Group>
        </Form.Item>

        <div className="flex items-center">
          <Form.Item
            name="fileOption"
            label={t('salary_translation.attach_file', { ns: 'forms' })}
            className="attach"
            rules={[
              {
                required: true,
                message: t('formValidation.required', { ns: 'forms' }),
              },
            ]}
          >
            <Upload
              accept=".pdf"
              onRemove={handleRemove}
              maxCount={1}
              fileList={fileList}
              name="file"
              multiple={false}
              beforeUpload={(file) => {
                if (file.size / 1000000 > 5) {
                  notification.error({
                    message: t('salaryTranslation.error.file_too_large', { ns: 'messages' }),
                    placement: 'bottomRight',
                  });
                  return false;
                }

                if (file.type !== 'application/pdf') {
                  notification.error({
                    message: t('salaryTranslation.error.pdf_only', { ns: 'messages' }),
                    placement: 'bottomRight',
                  });
                  return false;
                }

                setFileList([file]);
                return false;
              }}
            >
              <Button
                id="uploadButton"
                icon={<UploadOutlined />}
                loading={loading}
              >
                {loading ? <Spin size="small" /> : t('salary_translation.upload_pdf', { ns: 'forms' })}
              </Button>
              <Tooltip
                title={t('salary_translation.upload_tooltip', { ns: 'forms' })}
                color="#b1b1b1"
                placement="bottom"
                autoAdjustOverflow={true}
              >
                <QuestionCircleOutlined className="ml-2 cursor-pointer text-gray-400" />
              </Tooltip>
            </Upload>
          </Form.Item>
        </div>

        <div className="flex justify-end" style={{ marginTop: '30px' }}>
          <Button type="primary" id="submit" htmlType="submit" loading={loading} className="btn-primary">
            {t('common.submit', { ns: 'forms' })}
          </Button>
        </div>
      </Form>
    </div>
  );
}
